// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import axios from "axios";
import config from "../config";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userTier, setUserTier] = useState(1);
  const [uploadUsage, setUploadUsage] = useState(null);

  const fetchUploadUsage = async (email, options = {}) => {
    try {
      const base = `${config.apiBaseUrl}/api/user-uploads/usage?email=${encodeURIComponent(email)}`;
      // Cache-bust when refetching after payment so the banner shows updated lines
      const url = options.bustCache ? `${base}&_t=${Date.now()}` : base;
      const response = await axios.get(url);
      if (response.data) {
        setUploadUsage(response.data);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching upload usage:", error);
    }
    return undefined;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && currentUser.emailVerified) {
        const userData = {
          email: currentUser.email,
          uid: currentUser.uid,
          emailVerified: currentUser.emailVerified
        };
        
        // Fetch user tier from backend
        try {
          const response = await axios.post(`${config.apiBaseUrl}/api/users/login`, {
            email: currentUser.email
          });
          
          if (response.data && response.data.tier) {
            setUserTier(response.data.tier);
            userData.tier = response.data.tier;
          }
          
          // Fetch upload usage information
          await fetchUploadUsage(currentUser.email);
          
        } catch (error) {
          console.error("Error fetching user tier:", error);
        }
        
        setUser(userData);
      } else {
        setUser(null);
        setUserTier(1);
        setUploadUsage(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const resetUploadUsage = async (emailOverride, options = {}) => {
    const email = emailOverride || user?.email;
    if (email) {
      return fetchUploadUsage(email, options);
    }
    return undefined;
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserTier(1);
      setUploadUsage(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      logout, 
      userTier, 
      uploadUsage, 
      resetUploadUsage 
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};